import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { startCase } from 'lodash';
import { CoreFieldSource } from '~/src/entities/project/types';
import { useSingleMatterSync } from '~/src/components/SingleMatterSync';

export type CoreFieldBadgeProps = {
  source: CoreFieldSource;
  isModifiedLocally?: boolean;
};

export const CoreFieldBadge: React.FC<CoreFieldBadgeProps> = ({
  source,
  isModifiedLocally = false,
}) => {
  const { sync, canSync } = useSingleMatterSync();
  const displayEntity = source.entity_type;
  const attributeName = startCase(source.source_field);
  const badgeText = `Clio Manage > ${displayEntity} > ${attributeName}`;

  // Show as linked only if not modified on server AND not modified locally (optimistic)
  const isLinked = !source.is_modified && !isModifiedLocally;

  return (
    <Box
      sx={{
        position: 'relative',
        mt: 0.5,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        component="span"
        sx={(theme) => ({
          display: 'inline-flex',
          marginRight: 0.5,
          '& svg': {
            width: 14,
            height: 14,
            color: theme.palette.grey[600],
          },
        })}
      >
        {isLinked ? (
          <LinkIcon data-testid="LinkIcon" />
        ) : (
          <LinkOffIcon data-testid="LinkOffIcon" />
        )}
      </Box>
      {isLinked ? (
        <Typography
          variant="caption"
          component="div"
          sx={(theme) => ({
            color: theme.palette.grey[600],
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          })}
        >
          {badgeText}
        </Typography>
      ) : canSync ? (
        <Link
          component="button"
          variant="caption"
          onClick={(e) => {
            e.stopPropagation();
            sync('coreField');
          }}
          sx={(theme) => ({
            color: theme.palette.blue[600],
            cursor: 'pointer',
            fontWeight: 600,
            '&:hover': {
              textDecoration: 'underline',
            },
          })}
        >
          Use Clio Manage data
        </Link>
      ) : (
        <Typography
          variant="caption"
          component="div"
          sx={(theme) => ({
            color: theme.palette.grey[600],
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          })}
        >
          Enable Clio Manage connection
        </Typography>
      )}
    </Box>
  );
};
